import React from 'react';
import CurrencyInput from 'react-currency-input-field';
import styled, { css } from 'styled-components';
import { StyledBadgeIcon, StyledBadgeText, StyledFieldset, StyledLabel } from 'styled/UI/StyledForm';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import sizes from 'theme/sizes';
import Icon from './Icon';

const CurrencyInputField = React.forwardRef(({
    value,
    placeholder,
    label,
    layout = 'straight',
    badgeText,
    badgeIcon,
    badgeIconColor,
    badgePosition,
    badgeIconBackground,
    isBadgeClickable,
    disabled,
    textIndent,
    id,
    name,
    onChange,
    onBadgeClick,
}, ref) => (
    <StyledFieldset>
        {label && (
            <StyledLabel layout={layout} htmlFor={id}>
                {label}
            </StyledLabel>
        )}

        <StyledInput
            placeholder={placeholder}
            value={value}
            decimalsLimit={2}
            intlConfig={{ locale: 'pt-BR', currency: 'BRL' }}
            layout={layout}
            textIndent={textIndent}
            ref={ref}
            disabled={disabled}
            onValueChange={onChange}
            id={id}
            name={name}
        />

        {badgeText && (
            <StyledBadgeText
                position={badgePosition}
                clickable={isBadgeClickable && !disabled}
                layout={layout}
                onClick={() => onBadgeClick()}
            >
                {badgeText}
            </StyledBadgeText>
        )}

        {badgeIcon && (
            <StyledBadgeIcon
                id='badge_icon'
                layout={layout}
                bgColor={badgeIconBackground}
                position={badgePosition}
                clickable={isBadgeClickable && !disabled}
                onClick={() => onBadgeClick()}
            >
                <Icon name={badgeIcon} color={badgeIconColor} />
            </StyledBadgeIcon>
        )}

    </StyledFieldset>
));

export default CurrencyInputField;

const StyledInput = styled(CurrencyInput)`
    width: 100%;
    outline-color: transparent;
    transition: all ease 0.15s;
    background: transparent;
    outline: transparent;
    color: ${colors.night};

    ${({ disabled }) => disabled && css`
        pointer-events: none;
    `}

    ${({ icon }) => icon && css`
        padding-left: ${sizes.large} !important;
    `}

    ${({ textIndent }) => textIndent && css`
        text-indent: ${textIndent};
        
        &::placeholder {
            text-indent: ${textIndent};
        }
    `}

    &::placeholder {
        color: ${colors.nightLight};
    }

    ${({ layout }) => layout === 'straight' && css`
        border: none;
        border-bottom: ${sizes.quark} solid ${colors.nightLight};
        padding: ${sizes.quark};
        font-weight: ${fonts.weight.bold};
        
        &::placeholder {
            font-weight: ${fonts.weight.regular};
        }

        &:focus {
            border-bottom: 2px solid ${colors.night}
        }
    `}

    /*
     * Espelha o layout 'squired' do StyledForm (usado pelo Fieldset). Antes este
     * campo era cinza sobre fundo cinza enquanto os vizinhos eram quase-pretos
     * sobre branco — convenção visual de campo desabilitado, então todo campo de
     * valor passava impressão de estar travado.
     */
    ${({ layout }) => layout === 'squired' && css`
        background: ${colors.neutralLight};
        border: 1.5px solid #D4D4D4;
        padding: 11px 14px;
        border-radius: 8px;
        margin-top: 6px;
        font-size: ${fonts.sizes.default};
        color: ${colors.nightDark};

        &:hover {
            border-color: ${colors.nightLight};
        }

        &:focus {
            border-color: ${colors.occasionalPurple};
            box-shadow: 0 0 0 3px ${colors.occasionalPurple}1A;
        }
    `}

    /* Depois do bloco acima, senão o estado desabilitado é sobrescrito por ele. */
    ${({ layout, disabled }) => layout === 'squired' && disabled && css`
        background: ${colors.neutralDark};
        border: 1.5px solid ${colors.neutralDark};
        color: ${colors.night};
        pointer-events: none;
    `}

    ${({ layout }) => layout === 'straight-center' && css`
        border: none;
        border-bottom: ${sizes.quark} solid ${colors.nightLight};
        padding: ${sizes.quark};
        font-weight: ${fonts.weight.bold};
        text-align: center;
        color: ${colors.neutralDark};
        
        &::placeholder {
            font-weight: ${fonts.weight.regular};
            text-align: center;
            color: ${colors.nightLight};
        }

        &:focus {
            border-bottom: 2px solid ${colors.neutralDark}
        }
    `}
   
    ${({ layout }) => layout === 'rounded' && css`
        border: 2px solid #ccc;
        border-radius: 10px;
        padding: ${sizes.mini};
        font-weight: ${fonts.weight.bold};
        background: transparent;
        color: ${colors.nightDark};

        &:focus {
            border: ${sizes.quark} solid ${colors.occasionalPurple};
        }
    `}
`